import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  IconButton,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Spinner,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { useRef, useState, RefObject } from 'react';
import { FaEllipsisV, FaUserMinus, FaUserShield, FaCrown } from 'react-icons/fa';
import { RiAddCircleLine } from 'react-icons/ri';
import { HelivanButton } from '../helivan/HelivanButton';
import { HelivanColor } from '../helivan/HelivanColor';
import { HelivanText } from '../helivan/HelivanText';
import {
  OrganizationRole,
  useGetOrganizationWithMembersQuery,
  useUpdateMemberRoleMutation,
  useRemoveMemberMutation,
  useRevokeInvitationMutation,
  useResendInvitationMutation,
  useTransferOwnershipMutation,
  useLeaveOrganizationMutation,
} from '../../generated/graphql';
import { useAuth } from '../../contexts/AuthContext';
import { InviteMemberModal } from './InviteMemberModal';

const getRoleBadgeColor = (role: string) => {
  switch (role) {
    case 'OWNER':
      return 'purple';
    case 'ADMIN':
      return 'blue';
    default:
      return 'gray';
  }
};

const formatDate = (date: string | Date) => {
  return new Date(date).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

export const OrganizationMembers = () => {
  const toast = useToast();
  const { backendUser, refreshBackendUser } = useAuth();
  const currentOrgId = backendUser?.currentOrganization?.id;

  const { data, loading, refetch } = useGetOrganizationWithMembersQuery({
    variables: { organizationId: currentOrgId || '' },
    skip: !currentOrgId,
  });

  const [updateMemberRole] = useUpdateMemberRoleMutation();
  const [removeMember] = useRemoveMemberMutation();
  const [revokeInvitation] = useRevokeInvitationMutation();
  const [resendInvitation] = useResendInvitationMutation();
  const [transferOwnership] = useTransferOwnershipMutation();
  const [leaveOrganization] = useLeaveOrganizationMutation();

  const { isOpen: isInviteOpen, onOpen: onInviteOpen, onClose: onInviteClose } = useDisclosure();
  const { isOpen: isConfirmOpen, onOpen: onConfirmOpen, onClose: onConfirmClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null) as RefObject<HTMLButtonElement>;

  const [confirmAction, setConfirmAction] = useState<{
    title: string;
    message: string;
    action: () => Promise<void>;
  } | null>(null);

  const org = data?.getOrganizationWithMembers;
  const currentUserRole = org?.currentUserRole;
  const canManageMembers = currentUserRole === 'OWNER' || currentUserRole === 'ADMIN';
  const canTransferOwnership = currentUserRole === 'OWNER';

  const handleRoleChange = async (userId: string, newRole: OrganizationRole) => {
    try {
      await updateMemberRole({
        variables: {
          input: {
            organizationId: currentOrgId!,
            userId,
            role: newRole,
          },
        },
      });
      toast({
        title: 'Role updated',
        status: 'success',
        duration: 2000,
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to update role',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleRemoveMember = (userId: string, userName: string) => {
    setConfirmAction({
      title: 'Remove Member',
      message: `Are you sure you want to remove ${userName} from this organization?`,
      action: async () => {
        await removeMember({
          variables: { organizationId: currentOrgId!, userId },
        });
        toast({
          title: 'Member removed',
          status: 'success',
          duration: 2000,
        });
        refetch();
      },
    });
    onConfirmOpen();
  };

  const handleTransferOwnership = (newOwnerId: string, newOwnerName: string) => {
    setConfirmAction({
      title: 'Transfer Ownership',
      message: `Are you sure you want to transfer ownership to ${newOwnerName}? You will become an Admin.`,
      action: async () => {
        await transferOwnership({
          variables: { organizationId: currentOrgId!, newOwnerId },
        });
        toast({
          title: 'Ownership transferred',
          status: 'success',
          duration: 2000,
        });
        refetch();
      },
    });
    onConfirmOpen();
  };

  const handleLeaveOrganization = () => {
    setConfirmAction({
      title: 'Leave Organization',
      message: 'Are you sure you want to leave this organization?',
      action: async () => {
        await leaveOrganization({
          variables: { organizationId: currentOrgId! },
        });
        await refreshBackendUser();
        toast({
          title: 'Left organization',
          status: 'success',
          duration: 2000,
        });
      },
    });
    onConfirmOpen();
  };

  const handleRevokeInvitation = async (invitationId: string) => {
    try {
      await revokeInvitation({
        variables: { invitationId },
      });
      toast({
        title: 'Invitation revoked',
        status: 'success',
        duration: 2000,
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to revoke invitation',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleResendInvitation = async (invitationId: string) => {
    try {
      await resendInvitation({
        variables: { invitationId },
      });
      toast({
        title: 'Invitation resent',
        status: 'success',
        duration: 2000,
      });
      refetch();
    } catch (error) {
      toast({
        title: 'Error',
        description: error instanceof Error ? error.message : 'Failed to resend invitation',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const executeConfirmAction = async () => {
    if (confirmAction) {
      try {
        await confirmAction.action();
      } catch (error) {
        toast({
          title: 'Error',
          description: error instanceof Error ? error.message : 'Action failed',
          status: 'error',
          duration: 3000,
        });
      }
    }
    onConfirmClose();
    setConfirmAction(null);
  };

  if (!currentOrgId) {
    return (
      <Box py={8} textAlign="center">
        <HelivanText variant="smallBody">
          Please select an organization to manage members.
        </HelivanText>
      </Box>
    );
  }

  if (loading) {
    return (
      <Flex justify="center" py={8}>
        <Spinner color={HelivanColor.text.helivanBlue} />
      </Flex>
    );
  }

  return (
    <VStack spacing={3} align="start" width="full">
      <Flex justify="space-between" width="full" align="center">
        <HStack spacing={3}>
          <HelivanText variant="subheader" colorOverride={HelivanColor.text.blueDark}>
            {org?.name}
          </HelivanText>
          <Badge colorScheme={getRoleBadgeColor(currentUserRole || '')} fontSize="10px">
            {currentUserRole}
          </Badge>
        </HStack>
        <HStack spacing={2}>
          {currentUserRole !== 'OWNER' && (
            <HelivanButton variant="ghost" size="xs" onClick={handleLeaveOrganization}>
              Leave
            </HelivanButton>
          )}
          {canManageMembers && (
            <HelivanButton variant="green" size="sm" onClick={onInviteOpen}>
              <HStack spacing={1}>
                <RiAddCircleLine color="white" size={14} />
                <HelivanText colorOverride="white" variant="smallBody">
                  Invite
                </HelivanText>
              </HStack>
            </HelivanButton>
          )}
        </HStack>
      </Flex>

      {/* Members Table */}
      <Box width="full" overflowX="auto">
        <Table variant="simple" size="sm">
          <Thead bg={HelivanColor.background.lightestGrey}>
            <Tr>
              <Th borderColor={HelivanColor.border.primary}>
                <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>Name</HelivanText>
              </Th>
              <Th borderColor={HelivanColor.border.primary}>
                <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>Email</HelivanText>
              </Th>
              <Th borderColor={HelivanColor.border.primary}>
                <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>Role</HelivanText>
              </Th>
              <Th borderColor={HelivanColor.border.primary}>
                <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>Joined</HelivanText>
              </Th>
              {canManageMembers && (
                <Th borderColor={HelivanColor.border.primary}>
                  <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>Actions</HelivanText>
                </Th>
              )}
            </Tr>
          </Thead>
          <Tbody>
            {org?.members.map((member) => {
              const isCurrentUser = member.id === backendUser?.id;
              const canModifyMember = canManageMembers && !isCurrentUser && member.role !== 'OWNER';
              const canPromoteToAdmin = canModifyMember && member.role === 'MEMBER';
              const canDemoteToMember = canModifyMember && member.role === 'ADMIN';

              return (
                <Tr key={member.id} _hover={{ bg: HelivanColor.background.lightestGrey }}>
                  <Td borderColor={HelivanColor.border.primary}>
                    <HStack>
                      <HelivanText variant="smallBody" colorOverride={HelivanColor.text.black}>
                        {member.fullName}
                      </HelivanText>
                      {isCurrentUser && (
                        <Badge colorScheme="green" fontSize="10px">You</Badge>
                      )}
                    </HStack>
                  </Td>
                  <Td borderColor={HelivanColor.border.primary}>
                    <HelivanText variant="smallBody">{member.email}</HelivanText>
                  </Td>
                  <Td borderColor={HelivanColor.border.primary}>
                    <Badge colorScheme={getRoleBadgeColor(member.role)} fontSize="10px">{member.role}</Badge>
                  </Td>
                  <Td borderColor={HelivanColor.border.primary}>
                    <HelivanText variant="smallBody">{formatDate(member.joinedAt)}</HelivanText>
                  </Td>
                  {canManageMembers && (
                    <Td borderColor={HelivanColor.border.primary}>
                      {canModifyMember && (
                        <Menu>
                          <MenuButton
                            as={IconButton}
                            icon={<FaEllipsisV />}
                            variant="ghost"
                            size="xs"
                            color={HelivanColor.text.blueMid}
                          />
                          <MenuList fontSize="sm">
                            {canPromoteToAdmin && (
                              <MenuItem
                                icon={<FaUserShield />}
                                onClick={() => handleRoleChange(member.id, OrganizationRole.Admin)}
                              >
                                Promote to Admin
                              </MenuItem>
                            )}
                            {canDemoteToMember && (
                              <MenuItem
                                icon={<FaUserShield />}
                                onClick={() => handleRoleChange(member.id, OrganizationRole.Member)}
                              >
                                Demote to Member
                              </MenuItem>
                            )}
                            {canTransferOwnership && (
                              <MenuItem
                                icon={<FaCrown />}
                                onClick={() => handleTransferOwnership(member.id, member.fullName)}
                              >
                                Transfer Ownership
                              </MenuItem>
                            )}
                            <MenuItem
                              icon={<FaUserMinus />}
                              color="red.500"
                              onClick={() => handleRemoveMember(member.id, member.fullName)}
                            >
                              Remove Member
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      )}
                    </Td>
                  )}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>

      {/* Pending Invitations */}
      {canManageMembers && org?.pendingInvitations && org.pendingInvitations.length > 0 && (
        <VStack spacing={3} align="start" width="full" pt={2}>
          <HelivanText variant="subheader" colorOverride={HelivanColor.text.blueMid}>
            Pending Invitations
          </HelivanText>
          <Box width="full" overflowX="auto">
            <Table variant="simple" size="sm">
              <Thead bg={HelivanColor.background.lightestGrey}>
                <Tr>
                  <Th borderColor={HelivanColor.border.primary}>
                    <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>Email</HelivanText>
                  </Th>
                  <Th borderColor={HelivanColor.border.primary}>
                    <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>Role</HelivanText>
                  </Th>
                  <Th borderColor={HelivanColor.border.primary}>
                    <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>Invited By</HelivanText>
                  </Th>
                  <Th borderColor={HelivanColor.border.primary}>
                    <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>Expires</HelivanText>
                  </Th>
                  <Th borderColor={HelivanColor.border.primary}>
                    <HelivanText variant="smallBody+" colorOverride={HelivanColor.text.blueMid}>Actions</HelivanText>
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {org.pendingInvitations.map((invitation) => (
                  <Tr key={invitation.id} _hover={{ bg: HelivanColor.background.lightestGrey }}>
                    <Td borderColor={HelivanColor.border.primary}>
                      <HelivanText variant="smallBody" colorOverride={HelivanColor.text.black}>
                        {invitation.email}
                      </HelivanText>
                    </Td>
                    <Td borderColor={HelivanColor.border.primary}>
                      <Badge colorScheme={getRoleBadgeColor(invitation.role)} fontSize="10px">{invitation.role}</Badge>
                    </Td>
                    <Td borderColor={HelivanColor.border.primary}>
                      <HelivanText variant="smallBody">{invitation.invitedByUser?.fullName}</HelivanText>
                    </Td>
                    <Td borderColor={HelivanColor.border.primary}>
                      <HelivanText variant="smallBody">{formatDate(invitation.expiresAt)}</HelivanText>
                    </Td>
                    <Td borderColor={HelivanColor.border.primary}>
                      <HStack spacing={2}>
                        <HelivanButton
                          variant="ghost"
                          size="xs"
                          onClick={() => handleResendInvitation(invitation.id)}
                        >
                          Resend
                        </HelivanButton>
                        <HelivanButton
                          variant="ghost"
                          size="xs"
                          onClick={() => handleRevokeInvitation(invitation.id)}
                        >
                          Revoke
                        </HelivanButton>
                      </HStack>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </VStack>
      )}

      {/* Invite Modal */}
      <InviteMemberModal
        isOpen={isInviteOpen}
        onClose={onInviteClose}
        organizationId={currentOrgId}
        onSuccess={() => refetch()}
        canInviteAdmin={currentUserRole === 'OWNER' || currentUserRole === 'ADMIN'}
      />

      {/* Confirmation Dialog */}
      <AlertDialog
        isOpen={isConfirmOpen}
        leastDestructiveRef={cancelRef}
        onClose={onConfirmClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              {confirmAction?.title}
            </AlertDialogHeader>
            <AlertDialogBody>{confirmAction?.message}</AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} variant="ghost" onClick={onConfirmClose}>
                Cancel
              </Button>
              <HelivanButton variant="primary" onClick={executeConfirmAction} ml={3}>
                Confirm
              </HelivanButton>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </VStack>
  );
};
